import apiClient from "@/lib/api-client";

export class BookService {
  // Basic CRUD operations
  static async getBooks(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page);
    if (params.per_page) searchParams.append('per_page', params.per_page);

    const queryString = searchParams.toString();
    const url = queryString ? `book?${queryString}` : 'book';
    
    const response = await apiClient.get(url);
    return response.data;
  }

  static async getBook(id) {
    const response = await apiClient.get(`book/${id}`);
    return response.data;
  }

  static async getBookDetails(id) {
    const response = await apiClient.get(`book/${id}/details`);
    return response.data;
  }

  static async createBook(data) {
    const response = await apiClient.post('book', data);
    return response.data;
  }

  static async updateBook(id, data) {
    const response = await apiClient.put(`book/${id}`, data);
    return response.data;
  }

  static async patchBook(id, data) {
    const response = await apiClient.patch(`book/${id}`, data);
    return response.data;
  }

  static async deleteBook(id) {
    const response = await apiClient.delete(`book/${id}`);
    return response.data;
  }

  // Search and filtering
  static async searchBooks(filters) {
    const response = await apiClient.post('book/search', filters);
    return response.data;
  }

  static async getBooksByCategory(category) {
    const response = await apiClient.get(`book/category/${category}`);
    return response.data;
  }

  static async getBooksByStatus(status) {
    const response = await apiClient.get(`book/status/${status}`);
    return response.data;
  }

  static async getPublishedBooks() {
    const response = await apiClient.get('book/published/list');
    return response.data;
  }

  // Publishing operations
  static async publishBook(id) {
    const response = await apiClient.patch(`book/${id}/publish`);
    return response.data;
  }

  static async unpublishBook(id) {
    const response = await apiClient.patch(`book/${id}/unpublish`);
    return response.data;
  }

  // Published book operations
  static async getPublishedBook(bookId) {
    const response = await apiClient.get(`book/${bookId}/published`);
    return response.data;
  }

  static async getPublishedBookById(publishedId) {
    const response = await apiClient.get(`book/published/${publishedId}`);
    return response.data;
  }

  // Clone operation
  static async cloneBook(id, newData = {}) {
    const response = await apiClient.post(`book/${id}/clone`, newData);
    return response.data;
  }

  // Statistics and metadata
  static async getStatistics() {
    const response = await apiClient.get('book/meta/statistics');
    return response.data;
  }

  static async getCategories() {
    const response = await apiClient.get('book/meta/categories');
    return response.data;
  }

  static async getTrainingTypes() {
    const response = await apiClient.get('book/meta/training-types');
    return response.data;
  }

  // Bulk operations
  static async bulkUpdateStatus(ids, status) {
    const response = await apiClient.patch('book/bulk/status', { ids, status });
    return response.data;
  }

  // Recent books
  static async getRecentBooks(limit = 10) {
    const response = await apiClient.get(`book/recent/list?limit=${limit}`);
    return response.data;
  }

  // Export
  static async exportBooks(filters = {}, format = 'json') {
    const response = await apiClient.post('book/export', { ...filters, format });
    return response.data;
  }

  // Import
  static async importBookFromJson(jsonData, overwriteExisting = false) {
    const response = await apiClient.post('book/import/json', {
      json_data: jsonData,
      overwrite_existing: overwriteExisting
    });
    return response.data;
  }

  // File operations
  static async getAllFiles() {
    const response = await apiClient.get('book/files/all');
    return response.data;
  }

  static async getFileStatistics() {
    const response = await apiClient.get('book/files/statistics');
    return response.data;
  }

  static async getFileUsageStats() {
    const response = await apiClient.get('book/files/usage-stats');
    return response.data;
  }

  static async getFile(fileId) {
    const response = await apiClient.get(`book/files/${fileId}`);
    return response.data;
  }

  static async getFileWithRelations(fileId) {
    const response = await apiClient.get(`book/files/${fileId}/relations`);
    return response.data;
  }

  static async getBookFiles(bookId) {
    const response = await apiClient.get(`book/${bookId}/files`);
    return response.data;
  }

  // BookSection Management (Unified structure for chapters and sections)
  static async getBookChapters(bookId) {
    const response = await apiClient.get(`book/sections/book/${bookId}/chapters`);
    return response.data;
  }

  static async createChapter(bookId, chapterData) {
    const response = await apiClient.post(`book/sections/book/${bookId}/chapters`, chapterData);
    return response.data;
  }

  static async getAllBookSections(bookId) {
    const response = await apiClient.get(`book/sections/book/${bookId}/all`);
    return response.data;
  }

  static async getSection(sectionId) {
    const response = await apiClient.get(`book/sections/${sectionId}`);
    return response.data;
  }

  static async updateSection(sectionId, sectionData) {
    console.log("service sectionId", sectionId);
    const response = await apiClient.put(`book/sections/${sectionId}`, sectionData);
    return response.data;
  }

  static async deleteSection(sectionId) {
    console.log("service sectionId", sectionId);

    if (!sectionId || sectionId === "undefined" || sectionId === "") {
      throw new Error("Invalid sectionId provided to deleteSection");
    }
    const response = await apiClient.delete(`book/sections/${sectionId}`);
    return response.data;
  }

  static async getchildren(parentSectionId) {
    const response = await apiClient.get(`book/sections/${parentSectionId}/children`);
    return response.data;
  }

  static async createChildSection(parentSectionId, sectionData) {
    const response = await apiClient.post(`book/sections/${parentSectionId}/children`, sectionData);
    return response.data;
  }

  static async reorderBookSections(bookId, sectionOrder) {
    const response = await apiClient.patch(`book/sections/book/${bookId}/reorder`, { order: sectionOrder });
    return response.data;
  }

  // Update book content order (for drag and drop functionality)
  static async updateBookContentOrder(bookId, contents) {
    const orderData = contents.map((content, index) => ({
      id: content.id,
      order: index + 1,
      type: content.type,
      name: content.name
    }));
    
    const response = await apiClient.patch(`book/${bookId}/content-order`, { 
      contents: orderData 
    });
    return response.data;
  }

  static async cloneSection(sectionId) {
    const response = await apiClient.post(`book/sections/${sectionId}/clone`);
    return response.data;
  }

  // File operations for sections
  static async attachFileToSection(sectionId, fileData) {
    const response = await apiClient.post(`book/sections/${sectionId}/attach-file`, fileData);
    return response.data;
  }

  static async removeFileFromSection(sectionId) {
    const response = await apiClient.delete(`book/sections/${sectionId}/remove-file`);
    return response.data;
  }

  static async linkFileToSection(sectionId, fileId) {
    const response = await apiClient.post(`book/sections/${sectionId}/link-file`, { file_id: fileId });
    return response.data;
  }

  static async getSectionWithFile(sectionId) {
    const response = await apiClient.get(`book/sections/${sectionId}/with-file`);
    return response.data;
  }

  // Cover image operations
  static async uploadCoverImage(bookId, formData) {
    const response = await apiClient.post(`book/${bookId}/cover-image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  static async uploadFile(file) {
    try {
      const formData = new FormData();
      formData.append('files[]', file);
      
      const response = await apiClient.post('/file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('File upload error:', error);
      throw error;
    }
  }

  // Legacy methods for backward compatibility (deprecated - use unified methods above)
  // These methods maintain the old parameter structure but delegate to unified methods
  static async getBookModules(bookId) {
    return this.getBookChapters(bookId);
  }

  static async createModule(bookId, moduleData) {
    return this.createChapter(bookId, moduleData);
  }

  

  static async deleteModule(bookId, moduleId) {
    return this.deleteSection(moduleId);
  }

  static async reorderModules(bookId, moduleOrder) {
    return this.reorderBookSections(bookId, moduleOrder);
  }

  static async getModuleSections(moduleId) {
    return this.getchildren(moduleId);
  }

  static async createSection(bookId, moduleId, sectionData) {
    return this.createChildSection(moduleId, sectionData);
  }

  static async reorderSections(bookId, moduleId, sectionOrder) {
    return this.reorderBookSections(bookId, sectionOrder);
  }

  static async attachFileToSectionWithModule(bookId, moduleId, sectionId, fileData) {
    return this.attachFileToSection(sectionId, fileData);
  }

  static async removeFileFromSectionWithModule(bookId, moduleId, sectionId) {
    return this.removeFileFromSection(sectionId);
  }

  static async linkFileToSectionWithModule(bookId, moduleId, sectionId, fileId) {
    return this.linkFileToSection(sectionId, fileId);
  }
}
